import { useState } from "react";
import { ChevronRight, Check, Loader2 } from "lucide-react";
import { activityIcon } from "./activityIcons";

interface ActivityGroupProps {
  activities: string[];
  /** True when the AI is still working (this group is live). */
  active?: boolean;
}

export function ActivityGroup({ activities, active = false }: ActivityGroupProps) {
  const [open, setOpen] = useState(false);

  if (activities.length === 0) return null;

  // Single activity — compact inline step
  if (activities.length === 1) {
    const Icon = activityIcon(activities[0]!);
    return (
      <div className="flex items-center gap-2 py-1.5 px-1">
        <div className="flex items-center justify-center w-4 h-4 shrink-0">
          {active ? (
            <Loader2 size={12} className="text-daydra-accent animate-spin" />
          ) : (
            <Check size={12} className="text-daydra-muted/60" />
          )}
        </div>
        <Icon size={12} className="text-daydra-muted/50 shrink-0" />
        <span className="text-xs text-daydra-muted truncate">{activities[0]}</span>
      </div>
    );
  }

  // Multiple activities — collapsible step list
  return (
    <div className="py-1">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 py-1 px-1 rounded-lg hover:bg-daydra-surface/40 transition-colors group"
      >
        <div className="flex items-center justify-center w-4 h-4 shrink-0">
          {active ? (
            <Loader2 size={12} className="text-daydra-accent animate-spin" />
          ) : (
            <Check size={12} className="text-daydra-muted/60" />
          )}
        </div>
        <ChevronRight
          size={11}
          className={`shrink-0 text-daydra-muted/50 transition-transform duration-200 ${open ? "rotate-90" : ""}`}
        />
        <span className="text-xs text-daydra-muted group-hover:text-daydra-dim transition-colors">
          {activities.length} steps
        </span>
        {!open && (
          <span className="text-xs text-daydra-muted/40 truncate">
            — {activities[activities.length - 1]}
          </span>
        )}
      </button>

      {open && (
        <div className="ml-3 mt-0.5 mb-0.5 border-l border-daydra-border/50 animate-fade-in">
          {activities.map((activity, i) => {
            const StepIcon = activityIcon(activity);
            const isLast = i === activities.length - 1;
            return (
              <div key={i} className="flex items-center gap-2 py-0.5 pl-3">
                <StepIcon size={11} className="text-daydra-muted/40 shrink-0" />
                <span className={`text-xs truncate ${isLast && active ? "text-daydra-dim" : "text-daydra-muted/60"}`}>
                  {activity}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
